import { FC, ReactElement, useEffect, useState, useContext } from 'react'
import styled, { useTheme } from 'styled-components'
import { useTransition, animated, config } from '@react-spring/web'
import { device } from '../../breakpoints'
import CopyUrl from 'components/CopyUrl'
import UserVote from 'components/UserVote'
import ReactPlayer from 'react-player'
import { YearContext } from 'contexts'
import { hasVoted } from '../../utils/voteStorageKey'
import { getCandidatImage } from '../../utils/getCandidatImage'
import Markdown from 'react-markdown'

interface InfoWindowProps {
  itemData: any
  handleClose: () => void
}

const InfoWindow: FC<InfoWindowProps> = ({
  itemData,
  handleClose,
}): ReactElement => {
  const year = useContext(YearContext)
  const isMobile = window.innerWidth < 768
  const [data, setData] = useState(itemData)
  const [textContainerRef, setTextContainerRef] =
    useState<HTMLDivElement | null>(null)
  const [imagesContainerRef, setImagesContainerRef] =
    useState<HTMLDivElement | null>(null)
  const [scrollBarRef, setScrollBarRef] = useState<HTMLDivElement | null>(null)
  const [scrollImagesBarRef, setScrollImagesBarRef] =
    useState<HTMLDivElement | null>(null)
  const theme = useTheme() as any
  const transitions = useTransition(itemData, {
    config: {
      ...config.gentle,
      clamp: true,
    },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    onRest: () => {
      if (!itemData) {
        setData(null)
      }
    },
  })

  const handleTextScroll = (e: any) => {
    scrollBarRef?.style.setProperty(
      'height',
      `${
        (e.target.scrollTop /
          (e.target.scrollHeight - window.innerHeight * 0.9)) *
          90 +
        10
      }%`
    )
  }

  const handleImagesScroll = (e: any) => {
    scrollImagesBarRef?.style.setProperty(
      'height',
      `${
        (e.target.scrollTop /
          (e.target.scrollHeight - window.innerHeight * 0.9)) *
          90 +
        10
      }%`
    )
  }

  const handleImagesXScroll = (e: any) => {
    console.log('s', e.target.scrollLeft)

    scrollImagesBarRef?.style.setProperty(
      'height',
      `${
        (e.target.scrollLeft /
          (e.target.scrollWidth - window.innerWidth * 0.9)) *
          90 +
        10
      }%`
    )
  }

  useEffect(() => {
    textContainerRef?.addEventListener('scroll', handleTextScroll)

    return () => {
      textContainerRef?.removeEventListener('scroll', handleTextScroll)
    }
  }, [textContainerRef])

  useEffect(() => {
    imagesContainerRef?.addEventListener(
      'scroll',
      isMobile ? handleImagesXScroll : handleImagesScroll
    )

    return () => {
      imagesContainerRef?.removeEventListener(
        'scroll',
        isMobile ? handleImagesXScroll : handleImagesScroll
      )
    }
  }, [imagesContainerRef])

  useEffect(() => {
    if (itemData) {
      setData(itemData)
      console.log(itemData)
    }
  }, [itemData])

  return (
    <>
      {transitions(
        (style, item) =>
          item &&
          data && (
            <Content
              style={{
                transform: style.opacity.to(
                  (v: any) => 'translate3d(-50%,' + (1 - v) * 100 + '%,0)'
                ),
              }}
            >
              <Close
                onClick={() => {
                  handleClose()
                }}
              >
                <div />
              </Close>

              {!isMobile && (
                <ImagesContainer ref={setImagesContainerRef}>
                  {new Array(3).fill(0).map((e, i) => (
                    <Image
                      key={i}
                      imageUrl={getCandidatImage(year, data.number, i, 'l')}
                    />
                  ))}
                  <ScrollIndicatorImages>
                    <ScrollBar ref={setScrollImagesBarRef} />
                  </ScrollIndicatorImages>
                </ImagesContainer>
              )}

              <TextContainer ref={setTextContainerRef}>
                {isMobile && (
                  <ImagesContainer ref={setImagesContainerRef}>
                    {new Array(3).fill(0).map((e, i) => (
                      <Image
                        key={i}
                        imageUrl={getCandidatImage(year, data.number, i, 'l')}
                      />
                    ))}
                  </ImagesContainer>
                )}

                <TextContent>
                  {isMobile && (
                    <ScrollIndicatorImages
                      style={{
                        transform: 'rotate(90deg)',
                        top: 0,
                        position: 'absolute',
                      }}
                    >
                      <ScrollBar ref={setScrollImagesBarRef} />
                    </ScrollIndicatorImages>
                  )}

                  <RankContainer>
                    {data.rank === 1 && <Rank rank={1}>Lauréat</Rank>}
                    {data.rank === 2 && <Rank rank={1}>Nominé</Rank>}
                    {data.rank === 3 && <Rank rank={1}>Nominé</Rank>}
                    {data.prix && <Rank rank={1}>{data.prix}</Rank>}
                  </RankContainer>

                  <TextCategory>{data.category}</TextCategory>
                  <TextTitle>{data.title}</TextTitle>

                  <Infos>
                    <Label>Architecte</Label>
                    <Value>{data.architect}</Value>
                    <Label>Maître d’ouvrage</Label>
                    <Value>{data.client}</Value>
                    {data.generalContractor.length > 3 && (
                      <>
                        <Label>Entreprise générale</Label>
                        <Value>{data.generalContractor}</Value>
                      </>
                    )}
                    <Label>Adresse</Label>
                    <Value>{data.location}</Value>
                    {data.label && (
                      <>
                        <Label>Label</Label>
                        <Value>{data.label}</Value>
                      </>
                    )}
                    <Label>Vote du public</Label>
                    <Value>
                      {' '}
                      <UserVote
                        disabled={
                          true
                          // hasVoted(year, data.category)
                        }
                        starDimension={isMobile ? '30px' : '20px'}
                        starEmptyColor='#ffffff44'
                        starHoverColor='#ffffff'
                        starRatedColor={theme.colorTextInverted}
                        tippId={data.id}
                        category={data.category}
                        handleUpdateRatingCount={(count: number) => {}}
                      />
                    </Value>
                  </Infos>

                  {data.videoId && (
                    <PageSection>
                      <TextLabel style={{ marginBottom: '0.65em' }}>
                        {data.videoTitle}
                      </TextLabel>
                      <PageText>
                        <ReactPlayer
                          style={{ aspectRatio: '1/1' }}
                          width={'100%'}
                          height='auto'
                          config={{
                            file: {
                              attributes: {
                                controlsList:
                                  'nodownload noplaybackrate pictureinpicture',
                              },
                            },
                          }}
                          playsinline
                          controls
                          // playing
                          // light={require(`../../images/interviews/${data.videoId}.jpg`)}
                          // playIcon={
                          //   <svg
                          //     version='1.1'
                          //     id='play'
                          //     xmlns='http://www.w3.org/2000/svg'
                          //     xmlnsXlink='http://www.w3.org/1999/xlink'
                          //     x='0px'
                          //     y='0px'
                          //     height='100px'
                          //     width='100px'
                          //     viewBox='0 0 100 100'
                          //     enable-background='new 0 0 100 100'
                          //     xmlSpace='preserve'
                          //   >
                          //     <path
                          //       fill='none'
                          //       stroke='#ffffff'
                          //       d='M49.9,2.5C23.6,2.8,2.1,24.4,2.5,50.4C2.9,76.5,24.7,98,50.3,97.5c26.4-0.6,47.4-21.8,47.2-47.7
                          //  C97.3,23.7,75.7,2.3,49.9,2.5'
                          //     />
                          //     <path
                          //       fill='#ffffff'
                          //       d='M38,69c-1,0.5-1.8,0-1.8-1.1V32.1c0-1.1,0.8-1.6,1.8-1.1l34,18c1,0.5,1,1.4,0,1.9L38,69z'
                          //     />
                          //   </svg>
                          // }
                          url={`https://unityvideo.appuser.ch/video/uv${data.videoId}h.mp4`}
                        />
                      </PageText>
                    </PageSection>
                  )}

                  <PageSection>
                    <CopyUrl
                      locale='fr'
                      title='Partager ce contenu'
                      text=''
                      url={window.location.href}
                      buttonColor={'#333333'}
                      textButtonColor={'white'}
                    />
                  </PageSection>

                  <PageSection>
                    <PageText>
                      <Description>
                        <Markdown>{data.text}</Markdown>
                      </Description>
                    </PageText>
                  </PageSection>

                  {data.juryText.length > 20 && (
                    <PageSection>
                      <TextLabel style={{ marginBottom: '0.65em' }}>
                        Appréciation du jury
                      </TextLabel>
                      <PageText>
                        <Description
                          dangerouslySetInnerHTML={{ __html: data.juryText }}
                        ></Description>
                      </PageText>
                    </PageSection>
                  )}

                  {data.photoCredits && (
                    <PageSection>
                      <TextLabel style={{ marginBottom: '0.65em' }}>
                        Photos
                      </TextLabel>
                      <PageText>{data.photoCredits}</PageText>
                    </PageSection>
                  )}

                  {/* 
                  <VoteContainer>
                    Votez pour ce projet
                    <UserVote
                      starDimension='30px'
                      starEmptyColor='#ffffff44'
                      starHoverColor='#ffffffff'
                      starRatedColor='#ffffff'
                      tippId={data.id}
                      handleUpdateRatingCount={(count: number) => {
                        console.log(count)
                      }}
                    />
                  </VoteContainer> */}
                </TextContent>
                <ScrollIndicator>
                  <ScrollBar ref={setScrollBarRef} />
                </ScrollIndicator>
              </TextContainer>
            </Content>
          )
      )}
      {transitions(
        (style, item) =>
          item && (
            <MaskOverlay
              onClick={() => {
                handleClose()
              }}
              style={style}
            />
          )
      )}
    </>
  )
}

const ScrollIndicator = styled.div`
  position: fixed;
  top: 50%;
  right: 5px;
  width: 5px;
  height: 50px;
  transform: translateY(-50%);
  background: #444444;
  border-radius: 5px;
  overflow: hidden;
`

const ScrollBar = styled.div`
  position: absolute;
  border-radius: 5px;
  width: 100%;
  height: 10%;
  top: 0;
  background: ${({ theme }) => theme.colorMain};
`

const ScrollIndicatorImages = styled(ScrollIndicator)`
  right: calc(50% - 10px);
  // background: #ffffff44;

  ${ScrollBar} {
    // background: white;
  }
`

const Rank = styled.div<{ rank: number }>`
  color: ${({ theme }) => theme.colorText};
  background-color: ${({ theme }) => theme.colorTextInverted};
  display: inline-block;
  font-size: 0.65em;
  padding: 7px 10px;
  border-radius: 50px;
  line-height: 1.2;

  margin-right: 0.5em;

  &:last-of-type {
    margin-right: 0;
  }
`

const RankContainer = styled.div`
  margin-bottom: 1em;
`

const Content = styled(animated.div)`
  position: fixed;
  width: 90%;
  max-width: 1600px;
  margin: 0 auto;
  height: 90dvh;
  bottom: 0;
  z-index: 3;
  background: #151515;
  color: ${({ theme }) => theme.colorTextInverted};
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  left: 50%;
  transform: translateX(-50%);
  // border-radius: 22px 22px 0 0;
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    width: 100%;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 1fr 1fr;
    width: 90%;
  }
`

const VoteContainer = styled.div`
  position: fixed;
  bottom: 0;
  padding: 20px 40px;
  background: ${({ theme }) => theme.colorPrimaryButtonBackground};
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.2);
  // border-radius: 16px 16px 0 0;
  z-index: 99;
  color: ${({ theme }) => theme.colorPrimaryButtonText};
`

const Infos = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding-bottom: 15px;
  margin-bottom: 15px;
  font-size: 1.2em;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 1fr 1fr;
  }
`

const Label = styled.div`
  padding-top: 10px;
  padding-bottom: 5px;
  border-top: 1px solid ${({ theme }) => theme.colorLine};
  font-size: 0.85em;
  color: ${({ theme }) => theme.colorGreyDark};
  line-height: 1;
`

const Value = styled.div`
  padding-top: 5px;
  padding-bottom: 5px;
  border-top: 1px solid ${({ theme }) => theme.colorLine};
  margin-bottom: 0;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    border-top: 0;
    padding-top: 0;
    margin-bottom: 10px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    border-top: 1px solid ${({ theme }) => theme.colorLine};
    padding-top: 5px;
    margin-bottom: 0;
  }
`

const PageText = styled.div`
  font-size: 1.1em;
  line-height: 1.4;
  font-weight: 400;
`

const PageSection = styled.div`
  padding-top: 1em;
  margin-top: 1em;
  border-top: 1px solid ${({ theme }) => theme.colorLine};
`

const Image = styled.div.attrs<{ imageUrl?: string }>(({ imageUrl }) => ({
  style: imageUrl
    ? { backgroundImage: `url(${imageUrl})` }
    : { backgroundColor: '#000' },
}))<{ imageUrl?: string }>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background-size: cover;
  background-position: center;
  display: block;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    display: inline-block;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    display: block;
  }
`

const MaskOverlay = styled(animated.div)`
  backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 2;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #222222;
    mask-image: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.75) 20%,
      rgba(0, 0, 0, 0.75)
    );
  }
`

const ImagesContainer = styled.div`
  display: block;
  position: relative;
  height: auto;
  white-space: nowrap;
  overflow-y: scroll;
  overflow-x: hidden;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    height: 45vh;
    overflow-x: scroll;
    overflow-y: hidden;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    height: auto;
    overflow-y: scroll;
    overflow-x: hidden;
  }
`

const TextContainer = styled.div`
  display: block;
  position: relative;

  color: ${({ theme }) => theme.colorTextInverted};
  overflow-y: scroll;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  height: 90vh;
  box-sizing: border-box;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
  }

  @media ${device.tablet}, @media ${device.laptop} {
  }
`

const TextContent = styled.div`
  padding: 10vh 50px;
  padding-bottom: 200px;
  max-width: 700px;
  margin: 0 auto;
  box-sizing: border-box;
  position: relative;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 10vh 15px;
    padding-bottom: 200px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 10vh 50px;
    padding-bottom: 200px;
  }
`

const TextTitle = styled.div`
  font-weight: 400;
  font-size: 3em;
  line-height: 1.2;
  margin: 50px 0 30px;
  margin-top: 15px;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 1.65em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 3em;
  }
`

const TextLabel = styled.div`
  font-weight: 400;
  font-size: 1em;
  line-height: 1.2;
  color: ${({ theme }) => theme.colorGreyDark};
`

const TextCategory = styled.div`
  font-weight: 400;
  font-size: 1em;
  line-height: 1.2;
  color: ${({ theme }) => theme.colorGreyDark};
`

const Description = styled.div``

const Close = styled.div`
  z-index: 99;
  position: fixed;
  width: 60px;
  height: 60px;
  background-color: ${({ theme }) => theme.colorPrimaryButtonBackground};
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.4s ease-in;

  &:hover {
    .close {
      opacity: 1;
    }
  }

  div {
    display: inline-block;
    width: 32px;
    height: 32px;
    opacity: 0.75;
  }

  div:before,
  div:after {
    left: 28px;
    position: absolute;
    content: ' ';
    height: 33px;
    width: 2px;
    background-color: ${({ theme }) => theme.colorPrimaryButtonText};
  }
  div:before {
    transform: rotate(45deg);
  }
  div:after {
    transform: rotate(-45deg);
  }
`

export default InfoWindow
